<script setup lang="ts">
  import {
    computed,
    ref,
    watch,
    getCurrentInstance,
    onMounted,
    nextTick,
  } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { useCoupons } from '@/composition/use-coupons';
  import { useOrders } from '@/composition/use-orders';
  import { IDiscount } from '@/interfaces/IDiscount';
  import { IOrder } from '@/interfaces/IOrder';
  import { ICoupon } from '@/interfaces/ICoupon';
  import { DiscountType } from '@/enums/DiscountType';
  import { AlertType } from '@/interfaces/IAlert';
  import { debounce } from '@/utils';
  import { useTimeoutFn } from '@vueuse/core';

  interface Props {
    mode: 'create' | 'edit';
    discount?: IDiscount;
    defaultType?: DiscountType;
    order?: IOrder;
  }

  const props = withDefaults(defineProps<Props>(), {
    mode: 'create',
    defaultType: DiscountType.Manual,
  });

  const modals = useModal();
  const ui = useUi();
  const couponsHelper = useCoupons();
  const ordersHelper = useOrders();
  const { proxy } = getCurrentInstance()!;

  // Tab state
  const activeTab = ref(0);

  // Manual discount form data
  const isPercentage = ref(props.discount?.isPercentage ?? true);
  const value = ref(props.discount?.value ?? 0);
  const type = ref(props.discount?.type ?? props.defaultType);

  // Coupon form data
  const couponCode = ref('');
  const validating = ref(false);
  const validatedCoupon = ref<ICoupon | null>(null);
  const validationError = ref('');

  // Refs
  const valueInput = ref(null);

  // Computed values
  const title = computed(() =>
    props.mode === 'create' ? 'Agregar descuento' : 'Editar descuento'
  );

  const valueLabel = computed(() =>
    isPercentage.value ? 'Porcentaje' : 'Monto fijo'
  );

  const valueSuffix = computed(() => (isPercentage.value ? '%' : ''));

  const rules = computed(() => {
    if (isPercentage.value) {
      return [
        (v: number) => v > 0 || 'El descuento debe ser mayor a 0',
        (v: number) => v <= 100 || 'El descuento debe ser menor o igual a 100',
      ];
    } else {
      return [(v: number) => v > 0 || 'El monto debe ser mayor a 0'];
    }
  });

  // Form handling
  const form = ref(null);
  const valid = ref(false);

  // Function to select input text
  async function selectInputText() {
    await nextTick();
    if (valueInput.value) {
      const input = (valueInput.value as any).$el.querySelector('input');
      if (input) {
        input.focus();
        input.select();
      }
    }
  }

  const saving = ref(false);

  async function submit() {
    const isValid = await (form.value as any)?.validate();
    if (!isValid) return;

    saving.value = true;

    proxy.$parent.$emit('submit', {
      ...(props.mode === 'create' && { type: type.value }),
      isPercentage: isPercentage.value,
      value: value.value,
    });

    // TODO: Temporary fix for UI purposes, replace with async
    useTimeoutFn(() => {
      saving.value = false;
    }, 1000);
  }

  function cancel() {
    modals.hide('order-discount');
  }

  // Watch for type toggle to reset value when switching
  watch(isPercentage, () => {
    value.value = 0;
    selectInputText();
  });

  // Coupon validation
  async function validateCoupon() {
    if (!couponCode.value.trim() || !props.order) return;

    validating.value = true;
    validationError.value = '';
    validatedCoupon.value = null;

    try {
      const result = await couponsHelper.validateCouponForOrder(
        props.order,
        couponCode.value.trim()
      );

      if (result.valid && result.coupon) {
        validatedCoupon.value = result.coupon;
      } else {
        validationError.value = result.error || 'Cupón inválido';
      }
    } catch (error) {
      validationError.value = 'Error al validar cupón';
    } finally {
      validating.value = false;
    }
  }

  async function applyCoupon() {
    if (!validatedCoupon.value || !props.order || saving.value) return;

    try {
      saving.value = true;

      const orderRef = ref(props.order);
      const success = await couponsHelper.applyCouponToOrder(
        orderRef,
        couponCode.value.trim()
      );

      if (success) {
        modals.hide('order-discount');
      }
    } catch (e) {
      ui.serverError(e, 'Ocurrió un error al aplicar el cupón');
    } finally {
      saving.value = false;
    }
  }

  // Auto-validate on code input
  const debouncedValidate = debounce(validateCoupon, 500);
  watch(couponCode, (newCode) => {
    if (newCode) {
      couponCode.value = newCode.toUpperCase();
      debouncedValidate();
    } else {
      validatedCoupon.value = null;
      validationError.value = '';
    }
  });

  // Select input text when modal opens or tab changes
  onMounted(() => {
    if (activeTab.value === 0) {
      selectInputText();
    }
  });

  watch(activeTab, (newTab) => {
    if (newTab === 0) {
      nextTick(() => selectInputText());
    }
  });
</script>

<template>
  <div class="modal-order-discount">
    <div class="modal-header">
      <h3 class="modal-title">
        {{ mode === 'create' ? 'Agregar descuento' : 'Editar descuento' }}
      </h3>
    </div>

    <v-tabs v-model="activeTab" centered class="discount-tabs">
      <v-tab>
        <v-icon left small>mdi-pencil</v-icon>
        Manual
      </v-tab>
      <v-tab :disabled="mode === 'edit'">
        <v-icon left small>mdi-ticket-percent</v-icon>
        Cupón
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Manual discount tab -->
      <v-tab-item>
        <v-form ref="form" v-model="valid" @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-group">
              <v-btn-toggle
                v-model="isPercentage"
                mandatory
                color="primary"
                class="type-toggle"
              >
                <v-btn :value="true" small>
                  <v-icon left small>mdi-percent</v-icon>
                  Porcentaje
                </v-btn>
                <v-btn :value="false" small>
                  <v-icon left small>mdi-currency-usd</v-icon>
                  Monto fijo
                </v-btn>
              </v-btn-toggle>
            </div>

            <div class="form-group">
              <v-text-field
                ref="valueInput"
                v-model.number="value"
                :label="valueLabel"
                :suffix="valueSuffix"
                :rules="rules"
                type="number"
                outlined
                dense
                autofocus
                @keydown.enter="submit"
              />
            </div>
          </div>

          <div class="modal-footer">
            <v-btn text @click="cancel">Cancelar</v-btn>
            <v-btn
              color="primary"
              type="submit"
              :disabled="!valid"
              :loading="saving"
            >
              {{ mode === 'create' ? 'Agregar' : 'Guardar' }}
            </v-btn>
          </div>
        </v-form>
      </v-tab-item>

      <!-- Coupon tab -->
      <v-tab-item>
        <div class="modal-body">
          <div class="coupon-input-container">
            <v-text-field
              v-model="couponCode"
              label="Código del cupón"
              placeholder="Ej: VERANO20"
              outlined
              dense
              :loading="validating"
              :error-messages="validationError"
              @keyup.enter="validateCoupon"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  small
                  :disabled="!couponCode || validating"
                  @click="validateCoupon"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>

          <v-expand-transition>
            <div v-if="validatedCoupon" class="coupon-details">
              <v-alert type="success" outlined dense>
                <div class="coupon-info">
                  <div class="coupon-name">{{ validatedCoupon.name }}</div>
                  <div class="coupon-value">
                    Descuento:
                    {{ couponsHelper.formatCouponValue(validatedCoupon) }}
                  </div>
                  <div
                    v-if="validatedCoupon.description"
                    class="coupon-description"
                  >
                    {{ validatedCoupon.description }}
                  </div>
                </div>
              </v-alert>
            </div>
          </v-expand-transition>
        </div>

        <div class="modal-footer">
          <v-btn text @click="cancel">Cancelar</v-btn>
          <v-btn
            color="primary"
            :disabled="!validatedCoupon"
            :loading="saving"
            @click="applyCoupon"
          >
            Aplicar Cupón
          </v-btn>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<style scoped lang="scss">
  .modal-order-discount {
    .modal-header {
      @apply tw-px-6 tw-pt-6 tw-pb-2;

      .modal-title {
        @apply tw-text-xl tw-font-medium;
      }
    }

    .discount-tabs {
      @apply tw-mb-2;
    }

    .modal-body {
      @apply tw-px-6 tw-py-4 tw-space-y-4;
      min-height: 200px;

      .form-group {
        .type-toggle {
          @apply tw-w-full;

          ::v-deep .v-btn {
            @apply tw-flex-1;
          }
        }
      }

      .coupon-input-container {
        @apply tw-mb-4;
      }

      .coupon-details {
        .coupon-info {
          @apply tw-space-y-2;

          .coupon-name {
            @apply tw-font-medium tw-text-lg;
          }

          .coupon-value {
            @apply tw-text-base tw-font-medium tw-text-green-600;
          }

          .coupon-description {
            @apply tw-text-sm tw-text-gray-600;
          }
        }
      }
    }

    .modal-footer {
      @apply tw-px-6 tw-pb-6 tw-pt-2 tw-flex tw-justify-end tw-space-x-2;
    }
  }
</style>
